/**
 * Клиент API кабинета.
 *
 * Типы приходят из `@tms/contracts` — они генерируются из `01-api/tms-api-v1.yaml` и
 * руками не пишутся: расхождение типов фронта и контракта — самый дорогой класс ошибок
 * в интеграционном проекте (разд. 16 спецификации кабинета).
 *
 * Токен в коде не хранится. Он живёт в HttpOnly-cookie (NFR-922, ADR-0020), поэтому
 * каждый запрос идёт с `credentials: 'include'`, а обновление пары выполняется
 * отдельным вызовом при первом `401` — повторять запрос имеет смысл ровно один раз.
 */

export interface ProblemDetails {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail?: string;
  /** Ошибки по полям (`ValidationProblem` контракта): форма подсвечивает их на месте. */
  readonly errors?: readonly ProblemFieldError[];
  /** Код для поддержки: по нему запрос находится в журналах сервера. */
  readonly trace_id?: string;
}

export interface ProblemFieldError {
  readonly field: string;
  readonly code: string;
  readonly message?: string;
}

export class ApiError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.code;
  }
}

const BASE = '/api/v1';

let refreshing: Promise<boolean> | null = null;

/**
 * Кому сообщить, что сессия кончилась и обновить её не удалось.
 *
 * Без этого кабинет остаётся открытым с пустыми списками и ошибкой `invalid_token` на
 * каждом экране: запросы падают, а человек не понимает, что его просто разлогинило.
 */
let sessionExpired: (() => void) | null = null;

export function onSessionExpired(listener: (() => void) | null): void {
  sessionExpired = listener;
}

/**
 * Язык, на котором сервер отвечает об ошибках и отдаёт названия справочников (FR-560).
 *
 * Хранится в модуле, а не в React-состоянии: запрос уходит и из обработчиков, и из
 * таймеров, где контекста нет. Переключение языка меняет значение немедленно —
 * перезапуск приложения для этого не требуется (DoD блока 10).
 */
let language = 'uz';

export function setApiLanguage(locale: string): void {
  // Сервер принимает базовый тег: `uz-Latn` и `uz` для него одно и то же.
  language = locale;
}

export async function request<T>(
  path: string,
  init: RequestInit & { readonly retryOnUnauthorised?: boolean } = {},
): Promise<T> {
  const { retryOnUnauthorised = true, ...rest } = init;

  const response = await fetch(`${BASE}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'accept-language': language,
      // FormData несёт свой content-type с границей частей — его ставит браузер.
      ...(rest.body === undefined || rest.body instanceof FormData
        ? {}
        : { 'content-type': 'application/json' }),
      ...rest.headers,
    },
  });

  if (response.status === UNAUTHORISED && retryOnUnauthorised) {
    // Одна попытка обновления на все параллельные запросы: иначе десяток виджетов
    // экрана одновременно ротирует refresh-токен, и все, кроме первого, получат отказ.
    const renewed = await renewSession();

    if (renewed) {
      return request<T>(path, { ...init, retryOnUnauthorised: false });
    }

    sessionExpired?.();
  }

  if (!response.ok) {
    throw new ApiError(await toProblem(response));
  }

  if (response.status === NO_CONTENT) {
    return undefined as T;
  }

  const text = await response.text();

  return (text === '' ? undefined : JSON.parse(text)) as T;
}

export function get<T>(path: string, query: Record<string, unknown> = {}): Promise<T> {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }

  const suffix = search.toString();

  return request<T>(suffix === '' ? path : `${path}?${suffix}`);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function del<T = void>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

async function renewSession(): Promise<boolean> {
  refreshing ??= (async (): Promise<boolean> => {
    try {
      const response = await fetch(`${BASE}/web/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        // Токен лежит в HttpOnly-cookie и телу недоступен — в этом и смысл.
        body: JSON.stringify({ refresh_token: '' }),
      });

      return response.ok;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

async function toProblem(response: Response): Promise<ProblemDetails> {
  try {
    const body = (await response.json()) as Partial<ProblemDetails>;

    return {
      status: response.status,
      code: body.code ?? 'unknown',
      title: body.title ?? response.statusText,
      ...(body.detail === undefined ? {} : { detail: body.detail }),
      ...(Array.isArray(body.errors) ? { errors: body.errors } : {}),
      ...(typeof body.trace_id === 'string' ? { trace_id: body.trace_id } : {}),
    };
  } catch {
    return { status: response.status, code: 'unknown', title: response.statusText };
  }
}

const UNAUTHORISED = 401;
const NO_CONTENT = 204;
